"use client";

import React from "react";
import { ArrowRight, BookOpen, Clock, ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
function PostsGridConsumer({
  lang
}: {
  lang: Lang;
}) {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  const labels = {
    error: pick(lang, {
      en: "Unable to load related articles.",
      zh: "无法加载相关技术文章。",
      es: "No se pudieron cargar los artículos relacionados.",
      de: "Verwandte Artikel konnten nicht geladen werden.",
      fr: "Impossible de charger les articles associés.",
      pt: "Não foi possível carregar os artigos relacionados."
    }),
    retry: pick(lang, {
      en: "Retry loading",
      zh: "重试加载",
      es: "Reintentar",
      de: "Erneut versuchen",
      fr: "Réessayer",
      pt: "Tentar novamente"
    }),
    noArticles: pick(lang, {
      en: "No articles found",
      zh: "未找到相关文章",
      es: "No se encontraron artículos",
      de: "Keine Artikel gefunden",
      fr: "Aucun article trouvé",
      pt: "Nenhum artigo encontrado"
    }),
    noArticlesDesc: pick(lang, {
      en: "Check back soon for upcoming technical analyses and wedge wire filtration papers.",
      zh: "请稍后查看即将发布的技术分析与楔形丝过滤文献。",
      es: "Vuelva pronto para consultar próximos análisis técnicos sobre mallas de alambre en cuña.",
      de: "Besuchen Sie uns bald wieder für neue technische Analysen und Fachberichte zu Spaltsieben.",
      fr: "Revenez bientôt pour consulter les prochaines analyses techniques et études sur les grilles à fil profilé.",
      pt: "Volte em breve para conferir novas análises técnicas sobre telas de arame em cunha."
    }),
    fallbackCategory: pick(lang, {
      en: "Technical Article",
      zh: "技术文章",
      es: "Artículo técnico",
      de: "Fachartikel",
      fr: "Article technique",
      pt: "Artigo técnico"
    }),
    recentDate: pick(lang, {
      en: "Recent",
      zh: "近期",
      es: "Reciente",
      de: "Kürzlich",
      fr: "Récent",
      pt: "Recente"
    }),
    readArticle: pick(lang, {
      en: "Read article",
      zh: "阅读文章",
      es: "Leer artículo",
      de: "Artikel lesen",
      fr: "Lire l'article",
      pt: "Ler artigo"
    }),
    pageOf: (current: number, total: number) => {
      const locale = lang === "zh" ? "zh-CN" : lang === "de" ? "de-DE" : lang === "es" ? "es-ES" : lang === "fr" ? "fr-FR" : lang === "pt" ? "pt-BR" : "en-US";
      return pick(lang, {
        en: `Page ${current} of ${total}`,
        zh: `第 ${current} 页 / 共 ${total} 页`,
        es: `Página ${current} de ${total}`,
        de: `Seite ${current} von ${total}`,
        fr: `Page ${current} sur ${total}`,
        pt: `Página ${current} de ${total}`
      });
    },
    previous: pick(lang, {
      en: "Previous",
      zh: "上一页",
      es: "Anterior",
      de: "Zurück",
      fr: "Précédent",
      pt: "Anterior"
    }),
    next: pick(lang, {
      en: "Next",
      zh: "下一页",
      es: "Siguiente",
      de: "Weiter",
      fr: "Suivant",
      pt: "Próximo"
    }),
    imgAlt: pick(lang, {
      en: "Article feature image",
      zh: "文章特色配图",
      es: "Imagen destacada del artículo",
      de: "Beitragsbild",
      fr: "Image de l'article",
      pt: "Imagem destacada do artigo"
    })
  };
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col rounded-lg border border-border bg-card p-6">
            <div className="h-44 w-full rounded-md bg-muted" />
            <div className="mt-4 h-4 w-24 rounded bg-muted" />
            <div className="mt-3 h-6 w-3/4 rounded bg-muted" />
            <div className="mt-2 h-4 w-full rounded bg-muted" />
            <div className="mt-6 flex items-center justify-between pt-4 border-t border-border">
              <div className="h-4 w-20 rounded bg-muted" />
              <div className="h-4 w-16 rounded bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-lg border border-border bg-card p-8 text-center">
        <p className="mf-caption text-muted-foreground mb-4">
          {labels.error}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {labels.retry}
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="rounded-lg border border-border bg-card p-12 text-center">
        <BookOpen className="mx-auto size-10 text-muted-foreground/60 mb-3" aria-hidden="true" />
        <h3 className="text-base font-semibold text-foreground">
          {labels.noArticles}
        </h3>
        <p className="mt-1 mf-caption text-muted-foreground">
          {labels.noArticlesDesc}
        </p>
      </div>;
  }
  const locale = lang === "zh" ? "zh-CN" : lang === "de" ? "de-DE" : lang === "es" ? "es-ES" : lang === "fr" ? "fr-FR" : lang === "pt" ? "pt-BR" : "en-US";
  return <div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name : labels.fallbackCategory;
        const formattedDate = post.date ? new Date(post.date).toLocaleDateString(locale, {
          year: "numeric",
          month: "short",
          day: "numeric"
        }) : labels.recentDate;
        return <Card key={post.id} data-index={i} className="group flex flex-col justify-between overflow-hidden rounded-lg border border-border bg-card transition-colors duration-200 hover:border-primary/40">
              <div>
                {post.featuredImage ? <div className="relative mf-card-media mf-ratio-card w-full overflow-hidden">
                    <img src={post.featuredImage} alt={post.title || labels.imgAlt} loading="lazy" decoding="async" className="size-full object-cover" />
                  </div> : <div className="flex mf-card-media mf-ratio-card w-full items-center justify-center text-muted-foreground">
                    <FileText className="size-10 stroke-1" aria-hidden="true" />
                  </div>}

                <CardHeader className="pt-6">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <Badge variant="outline" className="font-normal mf-caption bg-card">
                      {categoryName}
                    </Badge>
                    <div className="flex items-center gap-1 mf-caption text-muted-foreground">
                      <Clock className="size-3.5" aria-hidden="true" />
                      <span>{formattedDate}</span>
                    </div>
                  </div>
                  <CardTitle className="text-lg font-semibold tracking-tight text-foreground transition-colors group-hover:text-primary">
                    {post.link ? <Link to={post.link} className="hover:underline">
                        {post.title}
                      </Link> : post.title}
                  </CardTitle>
                </CardHeader>

                {post.excerpt && <CardContent>
                    <CardDescription className="line-clamp-3 mf-caption leading-relaxed text-muted-foreground">
                      {post.excerpt.replace(/<[^>]+>/g, "")}
                    </CardDescription>
                  </CardContent>}
              </div>

              <CardFooter className="pt-4 border-t border-border mt-4">
                <Button asChild variant="ghost" size="sm" className="w-full justify-between px-0 font-medium hover:bg-transparent hover:text-primary">
                  <Link to={post.link || "#"}>
                    <span>{labels.readArticle}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>;
      })}
      </div>

      {(hasNext || hasPrev || totalPages > 1) && <div className="mt-10 flex items-center justify-between border-t border-border pt-6">
          <p className="mf-caption text-muted-foreground">
            {labels.pageOf(currentPage, totalPages || 1)}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="gap-1 mf-caption">
              <ChevronLeft className="size-4" aria-hidden="true" />
              {labels.previous}
            </Button>
            <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="gap-1 mf-caption">
              {labels.next}
              <ChevronRight className="size-4" aria-hidden="true" />
            </Button>
          </div>
        </div>}
    </div>;
}
export default function RelatedArticles() {
  const {
    lang
  } = useLanguage();
  const content = {
    badge: pick(lang, {
      en: "Technical Knowledge Base",
      zh: "技术知识库",
      es: "Base de conocimientos técnicos",
      de: "Technische Wissensdatenbank",
      fr: "Base de connaissances techniques",
      pt: "Base de conhecimento técnico"
    }),
    title: pick(lang, {
      en: "Related technical articles and guides",
      zh: "相关技术文章与指南",
      es: "Artículos y guías técnicas relacionadas",
      de: "Verwandte Fachartikel und Leitfäden",
      fr: "Articles et guides techniques associés",
      pt: "Artigos e guias técnicos relacionados"
    }),
    lead: pick(lang, {
      en: "Explore engineering insights, filtration principles, and wedge wire screen specifications.",
      zh: "探索工程见解、过滤原理以及楔形丝筛网技术规格。",
      es: "Explore análisis de ingeniería, principios de filtración y especificaciones de mallas en cuña.",
      de: "Erfahren Sie mehr über Konstruktionsgrundsätze, Filtrationsprinzipien und Spaltsieb-Spezifikationen.",
      fr: "Découvrez nos analyses techniques, principes de filtration et spécifications de grilles à fil profilé.",
      pt: "Explore percepções de engenharia, princípios de filtração e especificações de telas de arame em cunha."
    }),
    viewAll: pick(lang, {
      en: "View all resources",
      zh: "查看所有资源",
      es: "Ver todos los recursos",
      de: "Alle Ressourcen ansehen",
      fr: "Voir toutes les ressources",
      pt: "Ver todos os recursos"
    })
  };
  return <section id="relatedarticles" data-nav="light" className="border-t border-border bg-background mf-section">
      <div className="mf-container">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-2 gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
              <BookOpen className="size-3.5 text-primary" aria-hidden="true" />
              <span>{content.badge}</span>
            </Badge>
            <h2 className="mf-h2 text-foreground">
              {content.title}
            </h2>
            <p className="mt-2 mf-lead text-muted-foreground">
              {content.lead}
            </p>
          </div>
          <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex">
            <Link to="/TechnicalResourcesPage">{content.viewAll}</Link>
          </Button>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <PostsGridConsumer lang={lang} />
        </PostsProvider>

        <div className="mt-8 text-center sm:hidden">
          <Button asChild variant="outline" size="sm" className="w-full">
            <Link to="/TechnicalResourcesPage">{content.viewAll}</Link>
          </Button>
        </div>
      </div>
    </section>;
}